import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  SessionStatisticsReport,
  RBTPerformanceReport,
  SupervisionComplianceReport,
  BillingSummaryReport,
  LocationVerificationReport,
  TimesheetSummaryReport,
  DateRange,
} from '../models/Report';
import { SessionNote } from '../models/SessionNote';
import { Timesheet } from '../models/Timesheet';
import { LocationAuditLog } from './LocationAuditService';

export class ReportingService {
  private static instance: ReportingService;

  private constructor() {}

  static getInstance(): ReportingService {
    if (!ReportingService.instance) {
      ReportingService.instance = new ReportingService();
    }
    return ReportingService.instance;
  }

  /**
   * Generate Session Statistics Report
   */
  async generateSessionStatisticsReport(
    companyId: string,
    dateRange: DateRange,
    filters?: { rbtId?: string; clientId?: string }
  ): Promise<SessionStatisticsReport> {
    const { startDate, endDate } = dateRange;

    // Build query
    let q = query(
      collection(db, 'sessionNotes'),
      where('companyId', '==', companyId),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    const sessions = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as SessionNote)
    );

    // Apply additional filters
    let filteredSessions = sessions;
    if (filters?.rbtId) {
      filteredSessions = filteredSessions.filter(
        (s) => s.rbtId === filters.rbtId
      );
    }
    if (filters?.clientId) {
      filteredSessions = filteredSessions.filter(
        (s) => s.clientId === filters.clientId
      );
    }

    // Calculate statistics
    const totalSessions = filteredSessions.length;
    const completedSessions = filteredSessions.filter(
      (s) => s.status === 'approved'
    ).length;
    const pendingSessions = filteredSessions.filter(
      (s) => s.status === 'supervisor-review' || s.status === 'draft'
    ).length;
    const canceledSessions = 0; // No canceled status in the model

    // Calculate durations
    const durationsInMinutes = filteredSessions
      .filter((s) => s.timestamps?.sessionStart && s.timestamps?.sessionEnd)
      .map((s) => {
        const start = s.timestamps!.sessionStart!.toMillis();
        const end = s.timestamps!.sessionEnd!.toMillis();
        return (end - start) / (1000 * 60); // convert to minutes
      });

    const averageSessionDuration =
      durationsInMinutes.length > 0
        ? durationsInMinutes.reduce((a, b) => a + b, 0) /
          durationsInMinutes.length
        : 0;

    const totalSessionHours =
      durationsInMinutes.reduce((a, b) => a + b, 0) / 60;

    // Group by status
    const sessionsByStatus: Record<string, number> = {};
    filteredSessions.forEach((s) => {
      sessionsByStatus[s.status] = (sessionsByStatus[s.status] || 0) + 1;
    });

    // Group by RBT
    const rbtMap = new Map<string, { name: string; count: number; hours: number }>();
    filteredSessions.forEach((s) => {
      const existing = rbtMap.get(s.rbtId) || {
        name: s.rbtName,
        count: 0,
        hours: 0,
      };
      existing.count++;

      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        existing.hours += duration;
      }

      rbtMap.set(s.rbtId, existing);
    });

    const sessionsByRBT = Array.from(rbtMap.entries()).map(([rbtId, data]) => ({
      rbtId,
      rbtName: data.name,
      sessionCount: data.count,
      totalHours: Math.round(data.hours * 100) / 100,
    }));

    // Group by Client
    const clientMap = new Map<string, { name: string; count: number; hours: number }>();
    filteredSessions.forEach((s) => {
      const clientName = s.clientId; // TODO: Fetch actual client name
      const existing = clientMap.get(s.clientId) || {
        name: clientName,
        count: 0,
        hours: 0,
      };
      existing.count++;

      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        existing.hours += duration;
      }

      clientMap.set(s.clientId, existing);
    });

    const sessionsByClient = Array.from(clientMap.entries()).map(
      ([clientId, data]) => ({
        clientId,
        clientName: data.name,
        sessionCount: data.count,
        totalHours: Math.round(data.hours * 100) / 100,
      })
    );

    return {
      totalSessions,
      completedSessions,
      pendingSessions,
      canceledSessions,
      averageSessionDuration: Math.round(averageSessionDuration),
      totalSessionHours: Math.round(totalSessionHours * 100) / 100,
      sessionsByStatus,
      sessionsByRBT,
      sessionsByClient,
    };
  }

  /**
   * Generate RBT Performance Report
   */
  async generateRBTPerformanceReport(
    companyId: string,
    rbtId: string,
    dateRange: DateRange
  ): Promise<RBTPerformanceReport> {
    const { startDate, endDate } = dateRange;

    // Get session notes for this RBT
    const q = query(
      collection(db, 'sessionNotes'),
      where('companyId', '==', companyId),
      where('rbtId', '==', rbtId),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    const sessions = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as SessionNote)
    );

    if (sessions.length === 0) {
      return {
        rbtId,
        rbtName: 'Unknown',
        totalSessions: 0,
        completedSessions: 0,
        averageSessionDuration: 0,
        totalHours: 0,
        onTimeCheckIns: 0,
        lateCheckIns: 0,
        locationVerificationRate: 0,
        noteSubmissionRate: 0,
        averageNoteApprovalTime: 0,
        clientsServed: 0,
        billingCodes: [],
      };
    }

    const rbtName = sessions[0].rbtName;
    const totalSessions = sessions.length;
    const completedSessions = sessions.filter(
      (s) => s.status === 'approved'
    ).length;

    // Calculate durations
    const durationsInMinutes = sessions
      .filter((s) => s.timestamps?.sessionStart && s.timestamps?.sessionEnd)
      .map((s) => {
        const start = s.timestamps!.sessionStart!.toMillis();
        const end = s.timestamps!.sessionEnd!.toMillis();
        return (end - start) / (1000 * 60);
      });

    const averageSessionDuration =
      durationsInMinutes.length > 0
        ? durationsInMinutes.reduce((a, b) => a + b, 0) /
          durationsInMinutes.length
        : 0;

    const totalHours = durationsInMinutes.reduce((a, b) => a + b, 0) / 60;

    // Check-in analysis
    const onTimeCheckIns = sessions.filter((s) => {
      // Consider on-time if checked in within 15 minutes of appointment start
      return true; // TODO: Implement actual logic with appointments
    }).length;
    const lateCheckIns = totalSessions - onTimeCheckIns;

    // Location verification rate
    const verifiedLocations = sessions.filter(
      (s) => s.timestamps?.sessionCheckIn
    ).length;
    const locationVerificationRate =
      totalSessions > 0 ? (verifiedLocations / totalSessions) * 100 : 0;

    // Note submission rate
    const submittedNotes = sessions.filter(
      (s) => s.status !== 'draft'
    ).length;
    const noteSubmissionRate =
      totalSessions > 0 ? (submittedNotes / totalSessions) * 100 : 0;

    // Average approval time
    const approvalTimes = sessions
      .filter((s) => s.timestamps?.rbtDraftSubmitted && s.timestamps?.supervisorReviewCompleted)
      .map((s) => {
        const submitted = s.timestamps!.rbtDraftSubmitted!.toMillis();
        const approved = s.timestamps!.supervisorReviewCompleted!.toMillis();
        return (approved - submitted) / (1000 * 60 * 60); // hours
      });

    const averageNoteApprovalTime =
      approvalTimes.length > 0
        ? approvalTimes.reduce((a, b) => a + b, 0) / approvalTimes.length
        : 0;

    // Unique clients served
    const uniqueClients = new Set(sessions.map((s) => s.clientId));
    const clientsServed = uniqueClients.size;

    // Billing codes
    const billingCodeMap = new Map<string, { count: number; hours: number }>();
    sessions.forEach((s) => {
      const code = s.supervision?.billingCode || '97156'; // Default RBT code
      const existing = billingCodeMap.get(code) || { count: 0, hours: 0 };
      existing.count++;

      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        existing.hours += duration;
      }

      billingCodeMap.set(code, existing);
    });

    const billingCodes = Array.from(billingCodeMap.entries()).map(
      ([code, data]) => ({
        code,
        count: data.count,
        hours: Math.round(data.hours * 100) / 100,
      })
    );

    return {
      rbtId,
      rbtName,
      totalSessions,
      completedSessions,
      averageSessionDuration: Math.round(averageSessionDuration),
      totalHours: Math.round(totalHours * 100) / 100,
      onTimeCheckIns,
      lateCheckIns,
      locationVerificationRate: Math.round(locationVerificationRate * 100) / 100,
      noteSubmissionRate: Math.round(noteSubmissionRate * 100) / 100,
      averageNoteApprovalTime: Math.round(averageNoteApprovalTime * 100) / 100,
      clientsServed,
      billingCodes,
    };
  }

  /**
   * Generate Supervision Compliance Report
   */
  async generateSupervisionComplianceReport(
    companyId: string,
    dateRange: DateRange
  ): Promise<SupervisionComplianceReport> {
    const { startDate, endDate } = dateRange;

    // Get all session notes
    const q = query(
      collection(db, 'sessionNotes'),
      where('companyId', '==', companyId),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    const sessions = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as SessionNote)
    );

    // Filter sessions requiring supervision (based on appointment data)
    // For now, assume all sessions require supervision
    const sessionsRequiringSupervision = sessions;
    const totalSessionsRequiringSupervision = sessionsRequiringSupervision.length;

    // Sessions with supervision data
    const supervisedSessions = sessions.filter(
      (s) => s.supervision && s.supervision.supervisionDuration
    );
    const complianceRate =
      totalSessionsRequiringSupervision > 0
        ? (supervisedSessions.length / totalSessionsRequiringSupervision) * 100
        : 0;

    // Group by supervision method
    const supervisionByMethod: Record<string, number> = {};
    supervisedSessions.forEach((s) => {
      const method = s.supervision?.supervisionMethod || 'unknown';
      supervisionByMethod[method] = (supervisionByMethod[method] || 0) + 1;
    });

    // Group by RBT
    const rbtMap = new Map<
      string,
      {
        name: string;
        total: number;
        supervised: number;
        totalMinutes: number;
      }
    >();

    sessions.forEach((s) => {
      const existing = rbtMap.get(s.rbtId) || {
        name: s.rbtName,
        total: 0,
        supervised: 0,
        totalMinutes: 0,
      };
      existing.total++;

      if (s.supervision?.supervisionDuration) {
        existing.supervised++;
        existing.totalMinutes += s.supervision.supervisionDuration;
      }

      rbtMap.set(s.rbtId, existing);
    });

    const supervisionByRBT = Array.from(rbtMap.entries()).map(
      ([rbtId, data]) => ({
        rbtId,
        rbtName: data.name,
        totalSessions: data.total,
        supervisedSessions: data.supervised,
        totalSupervisionMinutes: data.totalMinutes,
        complianceRate:
          data.total > 0
            ? Math.round((data.supervised / data.total) * 10000) / 100
            : 0,
      })
    );

    // Group by Supervisor
    const supervisorMap = new Map<
      string,
      { name: string; count: number; totalMinutes: number }
    >();

    supervisedSessions.forEach((s) => {
      const existing = supervisorMap.get(s.supervisorId) || {
        name: s.supervisorName,
        count: 0,
        totalMinutes: 0,
      };
      existing.count++;
      existing.totalMinutes += s.supervision?.supervisionDuration || 0;
      supervisorMap.set(s.supervisorId, existing);
    });

    const supervisionBySupervisor = Array.from(supervisorMap.entries()).map(
      ([supervisorId, data]) => ({
        supervisorId,
        supervisorName: data.name,
        sessionsSupervised: data.count,
        totalSupervisionMinutes: data.totalMinutes,
        averageSupervisionTime:
          data.count > 0
            ? Math.round((data.totalMinutes / data.count) * 100) / 100
            : 0,
      })
    );

    return {
      totalSessionsRequiringSupervision,
      supervisedSessions: supervisedSessions.length,
      complianceRate: Math.round(complianceRate * 100) / 100,
      supervisionByMethod,
      supervisionByRBT,
      supervisionBySupervisor,
    };
  }

  /**
   * Generate Billing Summary Report
   */
  async generateBillingSummaryReport(
    companyId: string,
    dateRange: DateRange
  ): Promise<BillingSummaryReport> {
    const { startDate, endDate } = dateRange;

    // Get all session notes first, then filter for approved in memory
    let q = query(
      collection(db, 'sessionNotes'),
      where('companyId', '==', companyId),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    const allSessions = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as SessionNote)
    );

    // Filter for approved sessions only
    const sessions = allSessions.filter((s) => s.status === 'approved');

    // Calculate total billable hours
    let totalBillableHours = 0;
    sessions.forEach((s) => {
      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        totalBillableHours += duration;
      }
    });

    // Billing code descriptions
    const codeDescriptions: Record<string, string> = {
      '97156': 'RBT services (direct client sessions)',
      '97155': 'BCBA services (supervision, assessment)',
      '97153': 'Parent training (BCBA only)',
      '97158': 'Group notes (BCBA only)',
    };

    // Group by billing code
    const billingCodeMap = new Map<string, { count: number; hours: number }>();
    sessions.forEach((s) => {
      const code = s.supervision?.billingCode || '97156';
      const existing = billingCodeMap.get(code) || { count: 0, hours: 0 };
      existing.count++;

      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        existing.hours += duration;
      }

      billingCodeMap.set(code, existing);
    });

    const billingCodeBreakdown = Array.from(billingCodeMap.entries()).map(
      ([code, data]) => ({
        code,
        description: codeDescriptions[code] || 'Unknown code',
        count: data.count,
        hours: Math.round(data.hours * 100) / 100,
      })
    );

    // Group by RBT
    const rbtMap = new Map<
      string,
      { name: string; hours: number; codes: Record<string, number> }
    >();
    sessions.forEach((s) => {
      const existing = rbtMap.get(s.rbtId) || {
        name: s.rbtName,
        hours: 0,
        codes: {},
      };

      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        existing.hours += duration;

        const code = s.supervision?.billingCode || '97156';
        existing.codes[code] = (existing.codes[code] || 0) + duration;
      }

      rbtMap.set(s.rbtId, existing);
    });

    const byRBT = Array.from(rbtMap.entries()).map(([rbtId, data]) => ({
      rbtId,
      rbtName: data.name,
      totalHours: Math.round(data.hours * 100) / 100,
      billingCodes: Object.fromEntries(
        Object.entries(data.codes).map(([code, hours]) => [
          code,
          Math.round(hours * 100) / 100,
        ])
      ),
    }));

    // Group by client
    const clientMap = new Map<
      string,
      { name: string; sessions: number; hours: number }
    >();
    sessions.forEach((s) => {
      const existing = clientMap.get(s.clientId) || {
        name: s.clientId,
        sessions: 0,
        hours: 0,
      };
      existing.sessions++;

      if (s.timestamps?.sessionStart && s.timestamps?.sessionEnd) {
        const duration =
          (s.timestamps!.sessionEnd!.toMillis() -
            s.timestamps!.sessionStart!.toMillis()) /
          (1000 * 60 * 60);
        existing.hours += duration;
      }

      clientMap.set(s.clientId, existing);
    });

    const byClient = Array.from(clientMap.entries()).map(([clientId, data]) => ({
      clientId,
      clientName: data.name,
      totalHours: Math.round(data.hours * 100) / 100,
      totalSessions: data.sessions,
    }));

    return {
      totalBillableHours: Math.round(totalBillableHours * 100) / 100,
      totalRevenue: 0, // TODO: Calculate based on rates
      billingCodeBreakdown,
      byRBT,
      byClient,
      byInsuranceProvider: [], // TODO: Implement when insurance data is available
    };
  }

  /**
   * Generate Location Verification Report
   */
  async generateLocationVerificationReport(
    companyId: string,
    dateRange: DateRange
  ): Promise<LocationVerificationReport> {
    const { startDate, endDate } = dateRange;

    const q = query(
      collection(db, 'locationAuditLogs'),
      where('companyId', '==', companyId),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as LocationAuditLog)
    );

    const totalCheckIns = logs.length;
    const verifiedCheckIns = logs.filter((l) => l.isVerified).length;
    const failedVerifications = logs.filter(
      (l) => !l.isVerified && l.status === 'rejected'
    ).length;
    const pendingApprovals = logs.filter(
      (l) => !l.isVerified && l.status === 'flagged'
    ).length;

    const verificationRate =
      totalCheckIns > 0 ? (verifiedCheckIns / totalCheckIns) * 100 : 0;

    // Calculate average distance deviation
    const deviations = logs
      .filter((l) => l.distance !== undefined)
      .map((l) => l.distance);
    const averageDistanceDeviation =
      deviations.length > 0
        ? deviations.reduce((a, b) => a + b, 0) / deviations.length
        : 0;

    // Flagged locations (out of range)
    const flaggedLocations = logs
      .filter((l) => !l.isVerified)
      .map((l) => ({
        sessionNoteId: l.sessionNoteId,
        rbtName: l.rbtName || 'Unknown',
        clientName: 'Unknown', // TODO: Fetch actual client name from sessionNote
        date: l.createdAt.toDate(),
        scheduledLocation: l.appointmentAddress || 'N/A',
        actualLocation: `${l.checkInLatitude}, ${l.checkInLongitude}`,
        distance: l.distance || 0,
        status: (l.status === 'approved' ? 'approved' : l.status === 'rejected' ? 'rejected' : 'pending') as 'approved' | 'rejected' | 'pending',
      }));

    // Group by RBT
    const rbtMap = new Map<
      string,
      { name: string; total: number; verified: number; failed: number }
    >();
    logs.forEach((l) => {
      const rbtId = l.rbtId || 'unknown';
      const existing = rbtMap.get(rbtId) || {
        name: l.rbtName || 'Unknown',
        total: 0,
        verified: 0,
        failed: 0,
      };
      existing.total++;
      if (l.isVerified) existing.verified++;
      if (!l.isVerified && l.status === 'rejected') existing.failed++;
      rbtMap.set(rbtId, existing);
    });

    const byRBT = Array.from(rbtMap.entries()).map(([rbtId, data]) => ({
      rbtId,
      rbtName: data.name,
      totalCheckIns: data.total,
      verifiedCheckIns: data.verified,
      failedVerifications: data.failed,
      verificationRate:
        data.total > 0
          ? Math.round((data.verified / data.total) * 10000) / 100
          : 0,
    }));

    return {
      totalCheckIns,
      verifiedCheckIns,
      failedVerifications,
      pendingApprovals,
      verificationRate: Math.round(verificationRate * 100) / 100,
      averageDistanceDeviation: Math.round(averageDistanceDeviation * 100) / 100,
      flaggedLocations,
      byRBT,
    };
  }

  /**
   * Generate Timesheet Summary Report
   */
  async generateTimesheetSummaryReport(
    companyId: string,
    dateRange: DateRange
  ): Promise<TimesheetSummaryReport> {
    const { startDate, endDate } = dateRange;

    const q = query(
      collection(db, 'timesheets'),
      where('companyId', '==', companyId),
      where('periodStart', '>=', Timestamp.fromDate(startDate)),
      where('periodEnd', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    const timesheets = snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as Timesheet)
    );

    const totalTimesheets = timesheets.length;
    const pendingTimesheets = timesheets.filter(
      (t) => t.status === 'submitted'
    ).length;
    const approvedTimesheets = timesheets.filter(
      (t) => t.status === 'approved'
    ).length;
    const rejectedTimesheets = timesheets.filter(
      (t) => t.status === 'rejected'
    ).length;
    const paidTimesheets = timesheets.filter((t) => t.status === 'paid').length;

    let totalHours = 0;
    let totalRegularHours = 0;
    let totalOvertimeHours = 0;
    let totalMileage = 0;
    let totalMileageReimbursement = 0;

    timesheets.forEach((t) => {
      totalHours += t.totalHours;
      // Calculate regular vs overtime (40 hour threshold for W2 employees)
      if (t.employmentType === 'W2' && t.totalHours > 40) {
        totalRegularHours += 40;
        totalOvertimeHours += t.totalHours - 40;
      } else {
        totalRegularHours += t.totalHours;
      }
      totalMileage += t.totalMileage || 0;
      totalMileageReimbursement += t.totalMileageReimbursement || 0;
    });

    // Group by RBT
    const rbtMap = new Map<
      string,
      {
        name: string;
        count: number;
        hours: number;
        overtime: number;
        mileage: number;
        status: string;
      }
    >();

    timesheets.forEach((t) => {
      const existing = rbtMap.get(t.userId) || {
        name: t.userName,
        count: 0,
        hours: 0,
        overtime: 0,
        mileage: 0,
        status: t.status,
      };
      existing.count++;
      existing.hours += t.totalHours;
      // Calculate overtime for W2 employees
      if (t.employmentType === 'W2' && t.totalHours > 40) {
        existing.overtime += t.totalHours - 40;
      }
      existing.mileage += t.totalMileage || 0;
      rbtMap.set(t.userId, existing);
    });

    const byRBT = Array.from(rbtMap.entries()).map(([rbtId, data]) => ({
      rbtId,
      rbtName: data.name,
      timesheetCount: data.count,
      totalHours: Math.round(data.hours * 100) / 100,
      overtimeHours: Math.round(data.overtime * 100) / 100,
      mileage: Math.round(data.mileage * 100) / 100,
      status: data.status as 'pending' | 'approved' | 'rejected' | 'paid',
    }));

    // Group by week
    const weekMap = new Map<
      string,
      { start: Date; end: Date; count: number; hours: number; paid: boolean }
    >();

    timesheets.forEach((t) => {
      const weekKey = t.periodStart.toDate().toISOString().split('T')[0];
      const existing = weekMap.get(weekKey) || {
        start: t.periodStart.toDate(),
        end: t.periodEnd.toDate(),
        count: 0,
        hours: 0,
        paid: false,
      };
      existing.count++;
      existing.hours += t.totalHours;
      if (t.status === 'paid') existing.paid = true;
      weekMap.set(weekKey, existing);
    });

    const byWeek = Array.from(weekMap.values()).map((data) => ({
      weekStart: data.start,
      weekEnd: data.end,
      timesheetCount: data.count,
      totalHours: Math.round(data.hours * 100) / 100,
      totalPaid: data.paid,
    }));

    return {
      totalTimesheets,
      pendingTimesheets,
      approvedTimesheets,
      rejectedTimesheets,
      paidTimesheets,
      totalHours: Math.round(totalHours * 100) / 100,
      totalRegularHours: Math.round(totalRegularHours * 100) / 100,
      totalOvertimeHours: Math.round(totalOvertimeHours * 100) / 100,
      totalMileage: Math.round(totalMileage * 100) / 100,
      totalMileageReimbursement: Math.round(totalMileageReimbursement * 100) / 100,
      byRBT,
      byWeek,
    };
  }
}

export const reportingService = ReportingService.getInstance();
